<script lang="ts">
  import type {
    Item,
    ProcessedItem,
    SourceWithCount,
  } from '../../modules/item/item.interface';
  import type { CategoryConfig } from '../../modules/projects/project.interface';
  import { createEventDispatcher } from 'svelte';
  import {
    Button,
    Icon,
    Menu,
    List,
    ListItem,
    ProgressCircular,
  } from 'svelte-materialify';
  import {
    CategoryDisplayType,
    CategoryKey,
  } from '../../modules/projects/project.interface';
  import BackgroundImage from '../BackgroundImage.svelte';
  import ColorsPreview from '../ColorsPreview.svelte';
  import { ItemStatus, ItemAction } from '../../modules/item/item.constants';
  import { getFreshnessColorByDate } from '../../lib/utils';
  import { capitalize } from 'lodash';
  import moment from 'moment';
  import { sorting } from '../../store';

  export let item: Item;
  export let process: ProcessedItem = null;
  export let categoryConfig: CategoryConfig;
  export let actions = false;
  export let source: SourceWithCount = null;

  const dispatch = createEventDispatcher();
  const imageConfig = getPropConfig('image');
  let isHovered = false;

  $: actionList = getItemActions(item, actions, process);
  $: processOpts = getProcessOpts(process);
  $: getBadgeStyle($sorting.name);

  function getProcessOpts(process: ProcessedItem) {
    if (!process?.status) return;

    return {
      message: getProcessMessage(process.status),
      bg: process.status === ItemStatus.REPORT ? '#cbf4cb' : '#f0f0f0',
    };
  }

  function getPropConfig(propKey: string) {
    return categoryConfig.props.find((prop) => prop.key === propKey);
  }

  function isDisplay(displayType: CategoryDisplayType) {
    return categoryConfig.displayType === displayType;
  }

  function getItemActions(
    item: Item,
    actions: boolean,
    process: ProcessedItem
  ): ItemAction[] {
    if (!actions) return [];

    if (process?.status) {
      if (process.status === 'removed') return [];

      return [ItemAction.UNDO];
    }

    const itemActionMapByStatus = {
      [ItemStatus.REPORT]: [ItemAction.EDIT, ItemAction.QUEUE],
      [ItemStatus.QUEUE]: [ItemAction.SELECT, ItemAction.DISMISS],
      [ItemStatus.CANDIDATE]: [
        ItemAction.EDIT,
        ItemAction.SELECT,
        ItemAction.QUEUE,
        ItemAction.DISMISS,
        ItemAction.REMOVE,
      ],
    };

    return itemActionMapByStatus[item.status];
  }

  function getBadgeStyle(_) {
    if (!actions) return '';

    const selectedProp =
      $sorting.name === 'Date' ? item.createdAt : item.details?.publishedAt;

    const color = getFreshnessColorByDate(selectedProp);

    return `--color: ${color.textColor}; --background-color: ${color.color}`;
  }

  function getProcessMessage(status: typeof process.status) {
    if (!status) return '';

    if (status === ItemStatus.DISMISSED) {
      return capitalize(status);
    }

    if (status === 'removed') {
      return 'Removed';
    }

    return `Added to ${status}`;
  }

  function onAction(action: ItemAction) {
    dispatch('action', { action, item });
  }

  function onMouseEnter() {
    if (!actions) return;

    isHovered = true;
    dispatch('hovered-item', { actions: getItemActions(item, actions, process), item });
  }

  function onMouseLeave() {
    if (!actions) return;

    isHovered = false;
    dispatch('hovered-item', null);
  }
</script>

<div
  class="ri {categoryConfig.displayType}"
  class:loading={process?.loading}
  class:processed={process?.status && process.status !== item.status}
  class:hovered={isHovered}
  style={getBadgeStyle($sorting.name)}
  on:mouseenter={onMouseEnter}
  on:mouseleave={onMouseLeave}
>
  <p class="ri-process" style="background-color: {processOpts?.bg};">
    <span>{processOpts?.message}</span>
  </p>
  <p class="ri-loading">
    <ProgressCircular size={40} width={3} indeterminate />
  </p>
  {#if isDisplay(CategoryDisplayType.TWEET)}
    <div class="tweet">
      <div class="tweet-header">
        <img class="source-icon" src={item.details?.sourceIcon} alt="Source" />
        <div>
          <div class="source-header">{item.details?.authorName}</div>
          <div class="screen-name">@{item.details?.authorScreenName}</div>
        </div>
        <div class="tweet-actions">
          {#if actions}
            <div class="ri-actions">
              <Menu hover nudgeX={-50}>
                <div slot="activator">
                  <Button icon>
                    <Icon class="mdi mdi-dots-vertical" />
                  </Button>
                </div>
                <List>
                  {#each actionList as action}
                    <ListItem class="text-capitalize" on:click={() => onAction(action)}
                      >{action}</ListItem
                    >
                  {/each}
                </List>
              </Menu>
            </div>
          {/if}
        </div>
      </div>
      <div>
        <div class="">{item.title}</div>
      </div>
      {#if item.image}
        <BackgroundImage
          src={item?.image}
          ratio={imageConfig?.requirements?.ratio || '4x3'}
        />
      {/if}
      <div class="tweet-footer">
        <div class="tweet-retweet">
          <img class="icon-retweet" src="/img/icon-retweet.png" alt="Retweet" />
          <div>{item.details?.retweets}</div>
        </div>
        <div class="tweet-favorite">
          <img class="icon-favorite" src="/img/icon-favorite.png" alt="Favorite" />
          <div>{item.details?.favorites}</div>
        </div>
      </div>
      <div class="ri-moment">
        {moment(item.createdAt).fromNow()}
      </div>
    </div>
  {:else}
    <div class="ri-visual">
      {#if isDisplay(CategoryDisplayType.COLORS)}
        <ColorsPreview colors={item.details?.colors} />
      {:else if categoryConfig.key === 'job' && !item.image}
        <p class="ri-job-b">{(item.details?.description?.[0] || '').toUpperCase()}</p>
      {:else}
        <BackgroundImage
          src={item?.image}
          ratio={imageConfig?.requirements?.ratio || '4x3'}
          useResize={categoryConfig.key === 'behance'}
          className={categoryConfig.key === 'youtube' ? 'youtube' : ''}
        />
      {/if}
      {#if categoryConfig.key === 'instagram'}
        {#if item.details?.mediaType === 'carousel'}
          <img class="icon-gallery" src="/img/icon-gallery.png" alt="Gallery" />
        {:else if item.details?.mediaType === 'video'}
          <img class="icon-video" src="/img/icon-play.svg" alt="Video" />
        {/if}
      {/if}
      <div class="ri-moment">
        {#if categoryConfig.key !== 'job' && categoryConfig.key !== 'product'}
          {moment(
            $sorting.name === 'Date' ? item.createdAt : item.details?.publishedAt
          ).fromNow()}
        {/if}
      </div>
    </div>
    <div class="ri-details d-flex {categoryConfig.key}">
      <div class="ri-props">
        {#if item.details?.sourceIcon}
          <img class="ri-source-icon" src={item.details.sourceIcon} alt={item.title} />
        {/if}
        {#if categoryConfig.key === 'article'}
          <p class="ri-url">{item.details?.baseUrl}</p>
        {/if}
        {#if categoryConfig.displayType === CategoryDisplayType.DISCUSSION || categoryConfig.key === CategoryKey.DRIBBBLE || categoryConfig.key === CategoryKey.BEHANCE || categoryConfig.key === CategoryKey.BRANDING}
          <a class="ri-title" href={item.url} target="_blank">{item.title}</a>
          <p class="ri-subtitle text--secondary mt-2">
            {#if categoryConfig.key !== CategoryKey.DRIBBBLE}
              {item.details?.comments} comments /
            {/if}
            {item.details?.upvotes} upvotes
          </p>
        {:else}
          <p class="ri-title">{item.title}</p>
          {#if categoryConfig.key === 'nft' || categoryConfig.key === CategoryKey.FIGMA}
            <a class="ri-subtitle text--secondary" href={item.url} target="_blank">
              <img src={item.details?.authorImage} alt={item.details?.author} />
              <span>
                {item.details?.author}
              </span>
            </a>
            {#if categoryConfig.key === CategoryKey.FIGMA}
              <p class="ri-subtitle text--secondary mt-2">
                {item.details?.clicks} clicks /
                {item.details?.upvotes} upvotes
              </p>
            {/if}
          {:else}
            {#if categoryConfig.key === 'job' || categoryConfig.key === 'product'}
              <small class="ri-moment-text">{moment(item.createdAt).fromNow()}</small>
            {/if}
            <a
              class="ri-subtitle text--secondary link-text"
              href={item.url}
              target="_blank">{item.details?.description || item.url}</a
            >
            {#if categoryConfig.key === CategoryKey.PRODUCT}
              <p class="ri-subtitle text--secondary mt-2">
                {item.details?.comments} comments /
                {item.details?.upvotes} upvotes
              </p>
            {/if}
          {/if}
        {/if}
        {#if source}
          <p class="ri-subtitle text--secondary mt-4">
            <img src={source.image} alt={source.name} />
            <span> {source.name} </span>
          </p>
        {/if}
      </div>
      {#if actions}
        <div class="ri-actions">
          <Menu hover nudgeX={-50}>
            <div slot="activator">
              <Button icon>
                <Icon class="mdi mdi-dots-vertical" />
              </Button>
            </div>
            <List>
              {#each actionList as action}
                <ListItem class="text-capitalize" on:click={() => onAction(action)}
                  >{action}</ListItem
                >
              {/each}
            </List>
          </Menu>
        </div>
      {/if}
    </div>
  {/if}
</div>

<style lang="scss">
  .ri {
    display: flex;
    position: relative;
    flex-direction: column;
    margin-bottom: 24px;
    transition: background-color 0.1s;
    padding: 10px;

    &.hovered {
      background-color: #f0f0f0;
      border-radius: 6px;
    }

    .tweet-title {
      width: 95%;
      height: 100px;
      color: black;
    }
    .tweet-header {
      display: flex;
      margin-bottom: 20px;
    }

    .tweet-actions {
      width: 100%;
      display: flex;
      justify-content: flex-end;
    }

    .tweet-footer {
      display: flex;
      margin-top: 10px;
      justify-content: space-between;
      padding: 0 20px;
    }

    .source-icon {
      border-radius: 50%;
      width: 40px;
      height: 40px;
      margin-right: 20px;
    }

    .source-header {
      width: 150px;
    }

    .ri-moment {
      padding-left: 5px;
      color: white;
      border-radius: 0 0 5px 5px;
      background-color: var(--background-color);
      min-height: 8px;
    }

    .screen-name {
      color: #7d8aa8;
      font-size: 12px;
    }

    &.loading,
    &.processed {
      .ri-visual {
        opacity: 0.1;
      }

      .ri-details {
        opacity: 0.1;
      }

      .ri-loading {
        display: flex;
      }
    }

    &.processed:not(.loading) {
      .ri-process {
        display: flex;
      }

      .ri-loading {
        display: none;
      }
    }

    &.text {
      flex-direction: row;
      margin-bottom: 0;
      justify-content: start;

      .ri-process {
        span {
          font-size: 24px;
        }
      }

      .ri-visual {
        position: relative;
        flex: 0 0 50px;
        height: 57px;
        margin-right: 10px;
        border: solid 1px var(--background-color);
        .ri-moment {
          background-color: var(--background-color);
          min-height: 8px;
        }
      }
      .ri-moment-text {
        display: block;
        opacity: 0.6;
        font-size: 14px;
        line-height: 14px;
        margin-top: -6px;
      }

      .ri-details {
        margin-top: 0;
        flex: 1;
        max-width: 310px;

        .ri-props {
          flex: 0 0 280px;
          max-width: 280px;
        }

        .ri-title {
          margin-bottom: 6px;
        }
      }
    }
  }

  .ri-process {
    display: none;
    position: absolute;
    width: 100%;
    height: 100%;
    left: 0;
    top: 0;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    opacity: 0.5;

    span {
      font-size: 40px;
    }
  }

  .ri-loading {
    display: none;
    position: absolute;
    width: 100%;
    height: 100%;
    left: 0;
    top: 0;
    justify-content: center;
    align-items: center;
  }

  .ri-visual {
    position: relative;
    border-radius: 6px;
    overflow: hidden;
    box-shadow: 0 2px 4px rgba(black, 0.05);
    .ri-moment {
      color: var(--color);
      background-color: var(--background-color);
      padding-left: 10px;
      font-size: 14px;
      line-height: 24px;
    }
  }
  .ri-job-b {
    width: 50px;
    height: 50px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
  }

  .tweet {
    padding: 10px;
  }

  .tweet-retweet,
  .tweet-favorite {
    display: flex;
    align-items: center;
  }

  .icon-retweet,
  .icon-favorite {
    width: 16px;
    height: 16px;
    margin-right: 10px;
  }

  .icon-gallery {
    position: absolute;
    width: 27px;
    height: 27px;
    right: 21px;
    top: 21px;
    z-index: 1;
  }

  .icon-video {
    position: absolute;
    width: 16px;
    height: 16px;
    right: 21px;
    top: 21px;
    z-index: 1;
    filter: invert(1) drop-shadow(0 0 8px rgba(0, 0, 0, 0.35));
  }

  .ri-details {
    margin-top: 12px;

    .ri-subtitle img {
      width: 20px;
      height: 20px;
      margin-bottom: -4px;
      border-radius: 100%;
      margin-right: 8px;
    }

    &.nft {
      .ri-subtitle {
        display: flex;
        align-items: center;
        margin-top: 4px;
      }
    }

    &.instagram {
      margin-top: 6px;

      .ri-props {
        display: flex;
        align-items: center;
      }

      .ri-subtitle {
        display: none;
      }
    }

    &.article {
      .ri-url {
        display: inline-block;
        position: relative;
        top: -6px;
        margin-bottom: 4px;
        font-size: 13px;
        opacity: 0.6;
      }

      .ri-subtitle {
        opacity: 0.2;
      }

      .ri-title {
        margin-bottom: 12px;
      }
    }

    .ri-props {
      flex: 1;
      overflow: hidden;
    }

    .ri-source-icon {
      width: 20px;
      height: 20px;
      border-radius: 10px;
      margin-right: 10px;
    }

    .ri-title {
      display: -webkit-box;
      font-size: 17px;
      margin-bottom: 0;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }

    .ri-subtitle {
      width: 100%;
      display: block;
      font-size: 15px;
    }

    .ri-actions {
      opacity: 0.3;

      &:hover {
        opacity: 1;
      }
    }
  }
</style>
